/**
 * DailyNotesTab.jsx — FINAL FIXED VERSION (UTC SAFE)
 *
 * ✔ Uses createdAt exclusively for grouping
 * ✔ Correct UTC-based day keys (no timezone shift)
 * ✔ Correct readable header
 * ✔ Staff username displayed correctly
 * ✔ Optimistic UI consistent
 */

import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Divider,
    TextField,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { useQuery, useMutation, gql } from "@apollo/client";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

function normalizeContextText(raw = "") {
    let text = String(raw || "").trim();
    if (!text) return "";
    const replacements = [
        [/\bhen\b/gi, "he"],
        [/\beeds\b/gi, "needs"],
        [/\bjsut\b/gi, "just"],
        [/\bpaitent\b/gi, "patient"],
        [/\bdiganosis\b/gi, "diagnosis"],
        [/\bresdient\b/gi, "resident"],
        [/\bmedcation\b/gi, "medication"],
    ];
    replacements.forEach(([pattern, value]) => {
        text = text.replace(pattern, value);
    });
    text = text.replace(/\s+/g, " ").trim();
    if (text && !/[.!?]$/.test(text)) {
        text = `${text}.`;
    }
    return text;
}

const CONTEXT_SUGGESTIONS = [
    "Resident reported pain after lunch. Assisted with repositioning and hydration. Resident tolerated care and stated pain improved. Continue monitoring and notify provider if pain increases.",
    "Resident appeared anxious in the evening. Provided reassurance and a calm environment. Resident became less anxious and cooperative with care. Continue observation and document mood changes.",
    "Resident refused medication at scheduled time. Re-approached and educated resident; second attempt accepted. No adverse reaction observed. Continue MAR documentation and notify provider for repeated refusal.",
];

function buildLocalProgressDraft({ contextText }) {
    const context = normalizeContextText(contextText);
    const now = new Date();
    const stamp = now.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    return [
        `Resident observed on ${stamp}.`,
        context
            ? `Reported/observed context: ${context}`
            : "Resident status reviewed during routine monitoring and care interaction.",
        "Care interventions were provided per current plan and resident needs.",
        "Resident response and tolerance were monitored and documented at time of care.",
        "Will continue observation, document changes, and notify provider/family for significant updates."
    ].join(" ");
}

async function suggestProgressNoteDraft({ residentId, contextText, recentNotes }) {
    return buildLocalProgressDraft({ contextText });
}

/* ==========================
   GraphQL (date field REMOVED)
========================== */
const GET_DAILY_NOTES = gql`
  query GetDailyNotes($residentId: ID!) {
    getDailyNotes(residentId: $residentId) {
      id
      notes
      createdAt
      staff {
        id
        user {
          id
          username
        }
      }
    }
  }
`;

const ADD_DAILY_NOTE = gql`
  mutation AddDailyNote($data: DailyNoteInput!) {
    addDailyNote(data: $data) {
      id
      notes
      createdAt
      staff {
        id
        user {
          id
          username
        }
      }
    }
  }
`;

/* ==========================
   Helpers
========================== */

/** Convert createdAt timestamp → UTC YYYY-MM-DD */
const extractDayKey = (ts) => {
    try {
        const d = new Date(ts);
        if (isNaN(d)) return null;

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    } catch {
        return null;
    }
};

/** Show readable timestamp */
const formatDateTime = (ts) => {
    const d = new Date(ts);
    if (isNaN(d)) return "";

    return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    });
};

/** Show readable header WITHOUT timezone shift */
const formatDayHeader = (isoDay) => {
    const d = new Date(isoDay + "T00:00:00");

    return d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric"
    });
};

/* ==========================
   Component
========================== */
export default function DailyNotesTab({ residentId, staffId }) {
    const [openEditor, setOpenEditor] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [aiContext, setAiContext] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState("");
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiDraft, setAiDraft] = useState("");
    const [speechSupported, setSpeechSupported] = useState(false);
    const [speechListening, setSpeechListening] = useState(false);
    const [speechError, setSpeechError] = useState("");
    const [speechBlocked, setSpeechBlocked] = useState(false);
    const recognitionRef = useRef(null);

    const { data, loading } = useQuery(GET_DAILY_NOTES, {
        variables: { residentId }
    });

    const [addNote, { loading: saving }] = useMutation(ADD_DAILY_NOTE);

    const notes = data?.getDailyNotes || [];

    useEffect(() => {
        const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Recognition) {
            setSpeechSupported(false);
            return undefined;
        }
        setSpeechSupported(true);
        const recognition = new Recognition();
        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            let transcript = "";
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                transcript += `${event.results[i][0].transcript} `;
            }
            if (transcript.trim()) {
                setAiContext((prev) => `${prev}${prev ? " " : ""}${transcript.trim()}`);
            }
        };

        recognition.onerror = (event) => {
            const code = String(event?.error || "").toLowerCase();
            if (code === "not-allowed" || code === "service-not-allowed") {
                setSpeechBlocked(true);
                setSpeechError("Microphone permission is blocked. Allow mic access in browser site settings, then reload.");
            } else if (code === "no-speech") {
                setSpeechError("No speech detected. Please try again.");
            } else if (code === "audio-capture") {
                setSpeechError("No microphone found. Check your input device.");
            } else {
                setSpeechError(code ? `Speech error: ${code}` : "Speech recognition failed.");
            }
            setSpeechListening(false);
        };
        recognition.onend = () => {
            setSpeechListening(false);
        };

        recognitionRef.current = recognition;
        return () => {
            try {
                recognition.stop();
            } catch (_err) {
                // ignore
            }
        };
    }, []);

    /* ==========================
       Group notes by createdAt day (UTC)
    ========================== */
    const grouped = {};

    notes.forEach((note) => {
        const dayKey = extractDayKey(note.createdAt);
        if (!dayKey) return;

        if (!grouped[dayKey]) grouped[dayKey] = [];
        grouped[dayKey].push(note);
    });

    const sortedDates = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

    /* ==========================
       Add Note Handler
    ========================== */
    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        const now = new Date().toISOString(); // full timestamp

        await addNote({
            variables: {
                data: {
                    residentId,
                    staffId: staffId || null,
                    notes: newNote.trim()
                }
            },
            optimisticResponse: {
                addDailyNote: {
                    __typename: "ResidentDailyNote",
                    id: "temp-" + Date.now(),
                    notes: newNote.trim(),
                    createdAt: now,
                    staff: null
                }
            },
            update: (cache, { data: m }) => {
                const existing = cache.readQuery({
                    query: GET_DAILY_NOTES,
                    variables: { residentId }
                });

                cache.writeQuery({
                    query: GET_DAILY_NOTES,
                    variables: { residentId },
                    data: {
                        getDailyNotes: [
                            m.addDailyNote,
                            ...(existing?.getDailyNotes || [])
                        ]
                    }
                });
            }
        });

        setNewNote("");
        setAiContext("");
        setAiDraft("");
        setAiError("");
        setAiDialogOpen(false);
        setOpenEditor(false);
    };

    const handleAiSuggest = async () => {
        setAiError("");
        setAiLoading(true);
        try {
            const draft = await suggestProgressNoteDraft({
                residentId,
                contextText: aiContext,
                recentNotes: notes,
            });
            setAiDraft(draft);
            if (!openEditor) {
                setOpenEditor(true);
            }
        } catch (err) {
            setAiError(
                err?.message ||
                "Could not reach AI service. A local draft was used."
            );
        } finally {
            setAiLoading(false);
        }
    };

    const handleInsertAiDraft = () => {
        if (!aiDraft.trim()) return;
        setNewNote((prev) => (prev?.trim() ? `${prev.trim()}\n\n${aiDraft.trim()}` : aiDraft.trim()));
        setAiDialogOpen(false);
    };

    const handleUseContextSuggestion = (text) => {
        const normalized = normalizeContextText(text);
        setAiContext(normalized);
    };

    const handleToggleSpeech = () => {
        setSpeechError("");
        if (!speechSupported || !recognitionRef.current) {
            setSpeechError("Speech recognition is not supported in this browser.");
            return;
        }
        if (speechBlocked) {
            setSpeechError("Microphone permission is blocked. Allow mic access in browser site settings, then reload.");
            return;
        }
        try {
            if (speechListening) {
                recognitionRef.current.stop();
                setSpeechListening(false);
            } else {
                recognitionRef.current.start();
                setSpeechListening(true);
            }
        } catch (err) {
            setSpeechListening(false);
            setSpeechError(err?.message || "Could not start speech recognition.");
        }
    };

    /* ==========================
       UI
    ========================== */
    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Progress Notes
            </Typography>

            {!openEditor && (
                <Button variant="contained" onClick={() => setOpenEditor(true)}>
                    Add Progress Note
                </Button>
            )}

            {openEditor && (
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="New Note"
                        multiline
                        fullWidth
                        minRows={3}
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                    />

                    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleAddNote}
                            disabled={!newNote.trim() || saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Tooltip title="AI draft assistant">
                            <IconButton
                                color="primary"
                                onClick={() => setAiDialogOpen(true)}
                                aria-label="Open AI draft assistant"
                            >
                                <AutoFixHighIcon />
                            </IconButton>
                        </Tooltip>

                        <Button variant="outlined" onClick={() => setOpenEditor(false)}>
                            Cancel
                        </Button>
                    </Box>
                    {aiError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {aiError}
                        </Typography>
                    )}
                </Box>
            )}

            <Dialog
                open={aiDialogOpen}
                onClose={() => setAiDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>AI Draft Progress Note</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                        Context tips: include what happened, care provided, resident response, and follow-up plan.
                        Example: "Resident reported nausea at 8:30 AM, assisted with hydration and rest, tolerated care, continue monitoring and notify provider if symptoms worsen."
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Starter context options:
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                        {CONTEXT_SUGGESTIONS.map((item, idx) => (
                            <Button
                                key={`ctx-suggestion-${idx}`}
                                variant="outlined"
                                onClick={() => handleUseContextSuggestion(item)}
                                sx={{ justifyContent: "flex-start", textTransform: "none" }}
                            >
                                {idx + 1}. Use this context
                            </Button>
                        ))}
                    </Box>
                    <TextField
                        label="Context (optional)"
                        multiline
                        fullWidth
                        minRows={3}
                        value={aiContext}
                        onChange={(e) => setAiContext(e.target.value)}
                        placeholder="Example: resident restless at 6 PM, refused dinner first, accepted after cueing..."
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Button variant="outlined" onClick={handleAiSuggest} disabled={aiLoading}>
                        {aiLoading ? "Generating..." : "Generate Draft From Context"}
                    </Button>
                    <Button
                        variant="outlined"
                        color={speechListening ? "error" : "primary"}
                        onClick={handleToggleSpeech}
                        disabled={!speechSupported || speechBlocked}
                        sx={{ ml: 1 }}
                    >
                        {speechListening ? "Stop Dictation" : "Start Dictation"}
                    </Button>
                    {speechError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {speechError}
                        </Typography>
                    )}
                    {!speechSupported && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Speech dictation is not available in this browser.
                        </Typography>
                    )}
                    <TextField
                        label="Draft"
                        multiline
                        fullWidth
                        minRows={6}
                        value={aiDraft}
                        onChange={(e) => setAiDraft(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Insert appends this draft below your current New Note text and does not overwrite it.
                    </Typography>
                    {aiError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {aiError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAiDialogOpen(false)}>Close</Button>
                    <Button variant="contained" onClick={handleInsertAiDraft} disabled={!aiDraft.trim()}>
                        Insert Below Existing Note
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Grouped Notes */}
            <List sx={{ mt: 3 }}>
                {sortedDates.map((dayKey) => (
                    <React.Fragment key={dayKey}>
                        <Typography
                            sx={{
                                mt: 3,
                                mb: 1,
                                fontWeight: 700,
                                color: "#444"
                            }}
                        >
                            {formatDayHeader(dayKey)}
                        </Typography>

                        {grouped[dayKey].map((n) => (
                            <React.Fragment key={n.id}>
                                <ListItem alignItems="flex-start">
                                    <ListItemText
                                        primary={
                                            <Box sx={{ whiteSpace: "pre-line" }}>
                                                {n.notes}
                                            </Box>
                                        }
                                        secondary={
                                            <>
                                                {formatDateTime(n.createdAt)}
                                                {n.staff?.user && (
                                                    <Chip
                                                        size="small"
                                                        label={`By: ${n.staff.user.username}`}
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                            </>
                                        }
                                    />
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
}
